import BaseTemplate from '../../components/baseTemplate';
import { HttpStatusCode } from '../../../../utils/enums/http-method.enum';
import Input from '../../../../components/forms/input';
import { LoginService } from '../../../../services/login.service';
import Spinner from '../../../../components/ui/spinner';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { USER_ACTIONS } from '../../../../utils/enums/user-store-actions.enum';
import { useTranslation } from 'react-i18next';
import {
    ACCESS_USER_NAME,
    ASSOCIATIONS_NAME,
    COGNITO_TOKEN_NAME,
    DEALER_COUNTRY_NAME,
    DEFAULTDEALER_NAME,
    DISTRICTS_NAME,
    FULL_NAME,
    PARTYID_NAME,
    PERMISSIONS_NAME,
    PERMISSIONS_TOKEN_NAME,
    USER_NAME,
} from '../../../../config';
import {
    LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_EMAIL,
    LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_PASSWORD,
    LABEL_FIELD_ERROR_REQUEST_SERVICE,
    LABEL_TEXT_EMAIL,
    LABEL_TEXT_FORGOT_PASSWORD,
    LABEL_TEXT_LOGIN,
    LABEL_TEXT_PASSWORD,
    LABEL_TEXT_WELCOME,
} from '../../../../localization/resouceKeys';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

import { IUser } from 'src/utils/interfaces/user';
import { loginValidations } from './validations';
import { MaintenanceContext } from 'src/context/MaintenanceContext';
import { useFormik } from 'formik';
import { isSiteCanadian, isValidDealerPortal } from 'src/utils/helpers';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';

function isResponseEmpty(request: any): boolean {
    return request && request.status === HttpStatusCode.SUCCESS;
}

function isResponseLocked(request: any): boolean {
    return request && request.status === HttpStatusCode.LOCKED;
}

async function authRedirect(
    authData: any,
    username: string,
    password: string,
): Promise<any> {
    if (authData.isLocked) {
        return {
            type: USER_ACTIONS.SET_BLOCKED,
            payload: true,
        };
    } else if (authData.isDeviceRemembered) {
        sessionStorage.setItem(USER_NAME, username);
        sessionStorage.setItem(COGNITO_TOKEN_NAME, authData.cognitoToken);
        sessionStorage.setItem(
            PERMISSIONS_TOKEN_NAME,
            authData.permissionsToken,
        );
        const loginService = new LoginService();
        const request_profile = await loginService.getDealerProfile(
            authData.permissionsToken,
        );
        if (
            isResponseEmpty(request_profile) &&
            request_profile.data.userProfile
        ) {
            const {
                dsourceProfile,
                userProfile,
                userPermissions,
                associations,
                districts,
            } = request_profile.data.userProfile.userData;
            const { countryCode } =
                dsourceProfile?.user?.phoneNumbers?.[0] ?? {};
            const fullName = `${userProfile.userFirstName} ${userProfile.userLastName}`;
            sessionStorage.setItem(DEALER_COUNTRY_NAME, countryCode || 'NA');
            sessionStorage.setItem(FULL_NAME, fullName);
            sessionStorage.setItem(
                DEFAULTDEALER_NAME,
                userProfile.userDefaultDealer,
            );
            sessionStorage.setItem(
                PERMISSIONS_NAME,
                JSON.stringify(userPermissions),
            );
            sessionStorage.setItem(
                ASSOCIATIONS_NAME,
                JSON.stringify(associations),
            );
            sessionStorage.setItem(
                ACCESS_USER_NAME,
                JSON.stringify(userProfile.userIsFieldAccessUser),
            );
            sessionStorage.setItem(
                PARTYID_NAME,
                JSON.stringify(userProfile.userPartyId),
            );
            if (userProfile.userIsFieldAccessUser === true) {
                sessionStorage.setItem(
                    DISTRICTS_NAME,
                    JSON.stringify(districts),
                );
            }
            return {
                type: USER_ACTIONS.REMEMBERED,
                payload: {
                    username,
                    fullName,
                    password,
                    cognitoToken: authData.cognitoToken,
                    dealerCountry: countryCode || 'NA',
                    permissionsToken: authData.permissionsToken,
                    accessUser: userProfile.userIsFieldAccessUser,
                    districs: districts || [],
                    partyId: userProfile.userPartyId,
                },
            };
        } else {
            throw Error();
        }
    } else if (!authData.isDeviceRemembered && authData.isMfaRegistered) {
        return {
            type: USER_ACTIONS.MFA_REGISTERED,
            payload: {
                username,
                password,
            },
        };
    } else if (!authData.isDeviceRemembered && !authData.isMfaRegistered) {
        return {
            type: USER_ACTIONS.MFA_UNREGISTERED,
            payload: {
                username: username,
                secretUri: authData.secretUri,
                password: password,
            },
        };
    }
}

function updateErrorLabels(
    invalidCredentialError: boolean,
    invalidServiceError: boolean,
    t: any,
) {
    if (invalidCredentialError) {
        return {
            email: t(LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_EMAIL),
            password: t(LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_PASSWORD),
        };
    }
    if (invalidServiceError) {
        return {
            email: t(LABEL_FIELD_ERROR_REQUEST_SERVICE),
            password: t(LABEL_FIELD_ERROR_REQUEST_SERVICE),
        };
    }
    return {
        email: '',
        password: '',
    };
}

const Login = () => {
    const { maintenanceMode }: any = useContext(MaintenanceContext);
    const { dealerCountry } = useSelector((state: IUser) => {
        return state;
    });
    const { t, i18n } = useTranslation();
    const { labelText } = useLocalization();
    const navigation = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [invalidCredentialError, setInvalidCredential] =
        useState<boolean>(false);
    const [invalidServiceError, setInvalidService] = useState<boolean>(false);
    const [submitErrors, setSubmitErrors] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const {
        values,
        errors,
        touched,
        isValid,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldTouched,
    } = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: loginValidations,
        onSubmit: handlerSubmit,
    });
    const userDispatch = useDispatch();
    const inValidDealerPortal =
        dealerCountry !== null && !isValidDealerPortal(dealerCountry);
    const showInValidDealerPortalNotification =
        !maintenanceMode && inValidDealerPortal;

    function updateLabels(
        _invalidCredentialError: boolean,
        _invalidServiceError: boolean,
        _t: any,
    ) {
        return () => {
            setSubmitErrors(
                updateErrorLabels(
                    _invalidCredentialError,
                    _invalidServiceError,
                    _t,
                ),
            );
        };
    }
    i18n.on(
        'languageChanged',
        updateLabels(invalidCredentialError, invalidServiceError, t),
    );
    async function handlerSubmit(): Promise<void> {
        if (maintenanceMode) {
            return;
        }
        const loginService = new LoginService();
        try {
            setLoading(true);
            setInvalidCredential(false);
            setInvalidService(false);
            setSubmitErrors({
                email: '',
                password: '',
            });
            const request_auth = await loginService.dealerPortalLogin(
                values.email,
                values.password,
            );
            if (
                (isResponseEmpty(request_auth) && request_auth.data.isAuth) ||
                (isResponseLocked(request_auth) && request_auth.data.isLocked)
            ) {
                i18n.off('languageChanged', updateLabels);
                userDispatch(
                    await authRedirect(
                        request_auth.data,
                        values.email.toLowerCase(),
                        values.password,
                    ),
                );
            } else {
                setInvalidCredential(true);
                setSubmitErrors({
                    email: t(LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_EMAIL),
                    password: t(
                        LABEL_FIELD_ERROR_INVALID_OR_INCORRECT_PASSWORD,
                    ),
                });
            }
        } catch (e) {
            setInvalidService(true);
            setSubmitErrors({
                email: t(LABEL_FIELD_ERROR_REQUEST_SERVICE),
                password: t(LABEL_FIELD_ERROR_REQUEST_SERVICE),
            });
        } finally {
            setLoading(false);
        }
    }

    function forgotPassHandler() {
        navigation('/forgotPassword');
    }

    const getInValidCountryPortalMessage = () => {
        const isCanadian = isSiteCanadian();
        const origin = window.location.origin;
        let siteURL = origin;

        if (origin.includes('vwcredit.com')) {
            siteURL = origin.replace('vwcredit.com', 'vwfinance.ca');
        } else if (origin.includes('vwfinance.ca')) {
            return t(labelText.LABEL_TEXT_IN_VALID_DEALER_PORTAL_US, {
                siteURL: origin.replace('vwfinance.ca', 'vwcredit.com'),
            });
        }

        return t(labelText.LABEL_TEXT_IN_VALID_DEALER_PORTAL, {
            inValidCountryCode: isCanadian ? 'CA' : 'US',
            validCountryCode: isCanadian ? 'US' : 'CA',
            siteURL,
        });
    };

    useEffect(() => {
        const handleLanguageChange = () => {
            Object.keys(touched).forEach((field) => {
                setFieldTouched(field, true, true);
            });
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n, touched, errors, setFieldTouched]);

    return (
        <BaseTemplate>
            <div className={'form-wrapper'}>
                <div className={'main-form'}>
                    <div className={'header u-mb-xxsmall'}>
                        <span>{t(LABEL_TEXT_WELCOME)}</span>
                        <h4>Dealer Extranet</h4>
                    </div>
                    {maintenanceMode && (
                        <Notification
                            content=''
                            contentHtml={
                                labelText.LABEL_TEXT_MAINTENANCE_MESSAGE
                            }
                            type={NotificationType.WARNING}
                            visibility={true}
                            withoutClose
                            extraClass='u-mt-large'
                            setVisibility={() => {
                                /* */
                            }}
                        />
                    )}
                    {showInValidDealerPortalNotification && (
                        <Notification
                            content=''
                            contentHtml={getInValidCountryPortalMessage()}
                            type={NotificationType.ERROR}
                            visibility={true}
                            withoutClose
                            extraClass='in-valid-dealer-portal-wrapper'
                            setVisibility={() => {
                                /* */
                            }}
                        />
                    )}
                    <form
                        onSubmit={handleSubmit}
                        autoComplete={'off'}
                        className={'u-mt u-mb-large'}
                    >
                        <Input
                            labelName={t(LABEL_TEXT_EMAIL)}
                            type={'text'}
                            name={'email'}
                            maxlength={50}
                            handlerChange={handleChange}
                            handlerBlur={handleBlur}
                            value={values.email}
                            asErrors={errors.email}
                            asTouched={touched.email}
                            asSubmitError={submitErrors.email}
                        />
                        <Input
                            labelName={t(LABEL_TEXT_PASSWORD)}
                            type={showPassword ? 'text' : 'password'}
                            name={'password'}
                            icon={'eye'}
                            handlerChange={handleChange}
                            handlerBlur={handleBlur}
                            handlerIcon={() => {
                                setShowPassword(!showPassword);
                            }}
                            value={values.password}
                            asErrors={errors.password}
                            asTouched={touched.password}
                            asSubmitError={submitErrors.password}
                        />
                        <div className='sg-component-variant u-mt-xlarge'>
                            <button
                                className='main-btn c-btn c-btn--full'
                                disabled={!isValid || loading}
                                type={'submit'}
                            >
                                <span className='c-btn__text'>
                                    {loading ? (
                                        <Spinner />
                                    ) : (
                                        t(LABEL_TEXT_LOGIN)
                                    )}
                                </span>
                            </button>
                        </div>
                    </form>
                    <div
                        className={'u-mt-xxsmall password-forgot'}
                        onClick={forgotPassHandler}
                    >
                        <a className={'c-link'}>
                            <span className='c-link__body  '>
                                <span className='c-link__effect'>
                                    {t(LABEL_TEXT_FORGOT_PASSWORD)}
                                </span>
                            </span>
                        </a>
                    </div>
                </div>
            </div>
        </BaseTemplate>
    );
};

export default Login;
